import { useEffect, useMemo, useState } from "react";
import { Select, message } from "antd";
import { useNavigate } from "react-router-dom";
import Guard from "../../components/access-control/rbac/Guard";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import { useWorkspace } from "../../contexts/WorkspaceContext";

const ASSESSMENT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const assessmentProcesses = [
  {
    id: "ap-new-mj-2024",
    name: "New patient assessment process",
    patientType: "NEW",
    resident: "Margaret Johnson",
    startDate: "2024-05-06",
    endDate: "2024-06-05",
    status: "In progress",
    socialWorker: "Amanda Parker",
    attachedRecords: ["Initial State Assessment", "Meet and Greet", "Provider Review", "Client Onboarding", "Resident 30-Day Probation", "Daily Notes", "Negotiated Care Plan", "Payment Negotiation", "Final State Assessment", "State Confirmation", "Ongoing Care"],
  },
  {
    id: "ap-resident-mr-2024",
    name: "Resident annual assessment process",
    patientType: "RESIDENT",
    resident: "Mary Reynolds",
    startDate: "2024-05-12",
    endDate: "2024-06-12",
    status: "Review due",
    socialWorker: "Amanda Parker",
    attachedRecords: ["Yearly State Assessment", "Annual Residency Progress", "Negotiated Care Plan", "Payment Negotiation", "Final State Assessment", "State Confirmation", "Ongoing Care"],
  },
  {
    id: "ap-resident-jl-2024",
    name: "Resident change review",
    patientType: "RESIDENT",
    resident: "James Lee",
    startDate: "2024-04-22",
    endDate: "2024-05-18",
    status: "Confirmed",
    socialWorker: "Martin Lee",
    attachedRecords: ["Yearly State Assessment", "Annual Residency Progress", "Negotiated Care Plan", "Payment Negotiation", "Final State Assessment", "State Confirmation", "Ongoing Care"],
  },
  {
    id: "ap-new-as-2024",
    name: "New patient assessment process",
    patientType: "NEW",
    resident: "Aisha Sharma",
    startDate: "2024-03-28",
    endDate: "2024-04-27",
    status: "Closed",
    socialWorker: "Rachel Kim",
    attachedRecords: ["Initial State Assessment", "Meet and Greet", "Provider Review", "Client Onboarding", "Resident 30-Day Probation", "Daily Notes", "Negotiated Care Plan", "Payment Negotiation", "Final State Assessment", "State Confirmation", "Ongoing Care"],
  },
];

export const processStatusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("cancel") || normalized.includes("delete") || normalized.includes("decline")) return "high";
  if (normalized.includes("confirmed") || normalized.includes("closed")) return "low";
  if (normalized.includes("due")) return "high";
  return "blue";
};

const processWorkflowStatus = (process = {}) =>
  process?.workflowStatus || process?.processStatus || process?.status || "";

const isClosedProcessStatus = (process = {}) =>
  ["COMPLETED", "ARCHIVED", "CONFIRMED", "CLOSED", "CANCELLED", "CANCELED", "DELETED"].includes(
    String(processWorkflowStatus(process) || "").toUpperCase(),
  );

export const formatDate = (date) =>
  date ? new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Not set";

const residentNameFromAssessmentTitle = (process = {}) => {
  const title = String(process?.name || process?.title || process?.processName || process?.assessmentName || "").trim();
  const [, suffix = ""] = title.match(/\s+-\s+(.+)$/) || [];
  const name = suffix.trim();
  return name && !/^unnamed resident$/i.test(name) ? name : "";
};

const parseAssessmentPayloadObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const firstFilled = (...values) =>
  values.find((value) => String(value ?? "").trim()) ?? "";

const joinName = (...values) => values.map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const residentNameFromStateAssessment = (process = {}) => {
  const payload = parseAssessmentPayloadObject(process.initialStateAssessment || process.stateAssessment);
  const bio = {
    ...parseAssessmentPayloadObject(payload.bioData),
    ...parseAssessmentPayloadObject(payload.residentBioData),
    ...parseAssessmentPayloadObject(payload.bio),
    ...parseAssessmentPayloadObject(payload.personalInformation),
  };
  const personal = parseAssessmentPayloadObject(bio.personalInformation);
  const directName = firstFilled(
    bio.residentName,
    bio.fullName,
    bio.name,
    personal.residentName,
    personal.fullName,
    personal.name,
  );
  const partsName = joinName(
    firstFilled(bio.firstName, personal.firstName),
    firstFilled(bio.middleName, personal.middleName),
    firstFilled(bio.lastName, personal.lastName),
  );
  return directName || partsName;
};

export const processResidentName = (process, residentRecord = null) =>
  residentNameFor(residentRecord) !== "Unnamed resident" ? residentNameFor(residentRecord) :
  process?.resident?.name ||
  process?.resident?.fullName ||
  joinName(process?.resident?.firstName, process?.resident?.middleName, process?.resident?.lastName) ||
  process?.residentData?.name ||
  process?.residentData?.fullName ||
  joinName(process?.residentData?.firstName, process?.residentData?.middleName, process?.residentData?.lastName) ||
  process?.residentDetails?.name ||
  process?.residentDetails?.fullName ||
  joinName(process?.residentDetails?.firstName, process?.residentDetails?.middleName, process?.residentDetails?.lastName) ||
  (typeof process?.resident === "string" ? process.resident : "") ||
  process?.residentName ||
  process?.residentFullName ||
  process?.clientName ||
  residentNameFromStateAssessment(process) ||
  residentNameFromAssessmentTitle(process) ||
  "Unnamed resident";

export const processResidentId = (process) =>
  process?.residentId ||
  process?.clientId ||
  process?.resident?.id ||
  process?.resident?.residentId ||
  process?.residentData?.id ||
  process?.residentData?.residentId ||
  process?.residentDetails?.id ||
  process?.residentDetails?.residentId ||
  process?.stageReferences?.residentId ||
  process?.references?.residentId ||
  process?.residentSlug ||
  "";

const residentIdFor = (resident = {}) => resident?.id || resident?.residentId || resident?.clientId || "";

const residentNameFor = (resident = {}) =>
  resident?.name ||
  resident?.fullName ||
  resident?.residentName ||
  [resident?.firstName, resident?.middleName, resident?.lastName].filter(Boolean).join(" ") ||
  "Unnamed resident";

const residentDateOfBirthFor = (resident = {}) =>
  resident?.dateOfBirth ||
  resident?.dob ||
  resident?.birthDate ||
  resident?.residentDateOfBirth ||
  resident?.personalInformation?.dateOfBirth ||
  "";

const residentOptionLabel = (resident = {}) => {
  const dateOfBirth = residentDateOfBirthFor(resident);
  return dateOfBirth ? `${residentNameFor(resident)} - ${formatDate(dateOfBirth)}` : residentNameFor(resident);
};

export const processId = (process) =>
  process?.id || process?.assessmentProcessId || process?.processId || "";

const processDateKey = (process = {}) => {
  const value = process.processStartDate || process.startDate || process.assessmentDate || process.createdAt || "";
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toISOString().slice(0, 10);
};

const assessmentTitleKey = (process = {}) =>
  String(process?.name || process?.title || process?.processName || process?.assessmentName || process?.type || "")
    .replace(/\s+-\s+.+$/, "")
    .trim()
    .toLowerCase();

const processUpdatedTime = (process = {}) => {
  const date = new Date(process.updatedAt || process.modifiedAt || process.createdAt || process.processStartDate || process.assessmentDate || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const assessmentProcessListKey = (process = {}) => {
  const id = processId(process);
  if (id && isClosedProcessStatus(process)) return `id:${id}`;
  return [
    "assessment-process",
    processResidentId(process) || processResidentName(process),
    assessmentResidentType(process),
    processDateKey(process),
    assessmentTitleKey(process),
  ].map((value) => String(value || "").trim().toLowerCase()).join("|");
};

const dedupeAssessmentProcessList = (items = []) => {
  const byKey = new Map();
  (Array.isArray(items) ? items : []).forEach((process) => {
    const key = assessmentProcessListKey(process);
    const existing = byKey.get(key);
    if (!existing || processUpdatedTime(process) >= processUpdatedTime(existing)) {
      byKey.set(key, process);
    }
  });
  return Array.from(byKey.values());
};

export const processSocialWorkerName = (process) =>
  process?.socialWorkerName ||
  process?.socialWorker?.name ||
  [process?.socialWorkerFirstName || process?.socialWorker?.firstName, process?.socialWorkerLastName || process?.socialWorker?.lastName]
    .filter(Boolean)
    .join(" ") ||
  process?.socialWorker ||
  "Not assigned";

export const processRecords = (process) => {
  const records = process?.attachedRecords || process?.records || process?.assessmentRecords || [];
  if (Array.isArray(records)) return records;
  if (typeof records === "string" && records.trim()) {
    return records.split(",").map((record) => record.trim()).filter(Boolean);
  }
  return [];
};

const assessmentResidentType = (assessment) => {
  const raw = String(assessment?.patientType || assessment?.residentType || assessment?.type || "").toUpperCase();
  if (raw.includes("NEW")) return "NEW";
  if (raw.includes("ANNUAL") || raw.includes("RESIDENT") || raw.includes("EXISTING")) return "RESIDENT";
  return raw || "Not set";
};

const assessmentResidentTypeLabel = (assessment) => {
  const type = assessmentResidentType(assessment);
  if (type === "NEW") return "New Resident";
  if (type === "RESIDENT") return "Current Resident";
  return type;
};

const assessmentDisplayName = (assessment, residentRecord = null) => {
  const rawName = assessment?.name || assessment?.title || assessment?.processName || assessment?.assessmentName || "";
  const residentName = processResidentName(assessment, residentRecord);
  if (rawName && /\s+-\s+.+$/.test(rawName)) {
    return rawName.replace(/\s+-\s+.+$/, residentName ? ` - ${residentName}` : "");
  }
  return rawName || assessment?.type || "Resident assessment";
};

export const recordLabel = (record) =>
  typeof record === "string" ? record : record?.name || record?.title || record?.recordName || record?.recordType || "Assessment Record";

export const recordSlug = (record) =>
  typeof record === "string"
    ? assessmentRecordSlug(record)
    : record?.slug || assessmentRecordSlug(record?.recordType || record?.type || recordLabel(record));

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const probationYesterday = dateInputValue(shiftDate(new Date(), -1));
const probationTwoDaysAgo = dateInputValue(shiftDate(new Date(), -2));
const probationOlderDay = dateInputValue(shiftDate(new Date(), -5));

export const assessmentRecordSlug = (record) =>
  String(record || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const assessmentRecordDetails = {
  "ap-new-mj-2024": {
    "initial-assessment": {
      document: "State Initial Assessment.pdf",
      dateUploaded: "2024-05-06",
      uploadedBy: "Sarah Mitchell",
      socialWorker: "Amanda Parker",
      issuer: "Washington DSHS",
      notes: "<p><strong>Initial state assessment received.</strong></p><ul><li>Medication behavior, diagnosis, history, care needs, and client profile were included.</li><li>Provider review should compare state details against Meet and Greet findings.</li></ul>",
    },
    "meet-and-greet": {
      meetingDate: "2024-05-06",
      location: "Harborview Medical Center, Room 412",
      socialWorker: "Amanda Parker",
      facilitator: "Sarah Mitchell",
      careProviderStaff: ["Sarah Mitchell - Lead caregiver", "Daniel Reed - Care aide"],
      careCircle: [
        "Elaine Johnson - Daughter / PoA",
        "Robert Johnson - Son",
        "Maya Torres - Care taker",
        "Linda Green - Guardian contact",
      ],
      signedDocuments: ["Meet and Greet acknowledgement"],
      uploadedDocuments: ["Hospital discharge summary.pdf", "Medication list.pdf"],
      notes: "<p>Resident was <strong>alert, calm, and receptive</strong> to a home visit.</p><ul><li>Family emphasized mobility support and medication routines.</li><li>Care team should confirm transfer support needs during onboarding.</li></ul>",
      questions: [
        ["What daily routines make you feel most comfortable?", "Morning coffee, quiet breakfast, and afternoon phone call with daughter."],
        ["What support do you need with transfers or walking?", "Needs one-person standby assist when tired."],
        ["Are there foods, activities, or care approaches you prefer?", "Low-salt meals, music after lunch, and door slightly open at night."],
      ],
    },
    "client-onboarding": {
      meetingDate: "2024-05-08",
      location: "Olreadia Care Home, Maple Suite",
      socialWorker: "Amanda Parker",
      facilitator: "Sarah Mitchell",
      careProviderStaff: ["Sarah Mitchell - Lead caregiver", "Daniel Reed - Care aide", "Maya Torres - Medication aide"],
      careCircle: [
        "Elaine Johnson - Daughter / PoA",
        "Robert Johnson - Son",
        "Maya Torres - Care taker",
        "Linda Green - Guardian contact",
      ],
      signedDocuments: [
        "Admission agreement",
        "Resident rights and responsibilities",
        "Medication management consent",
        "HIPAA/privacy acknowledgment",
        "House policies",
      ],
      uploadedDocuments: ["Signed admission packet.pdf", "PoA documentation.pdf", "Medication consent.pdf"],
      notes: "<p><strong>Client onboarding completed at the home.</strong></p><ul><li>Family reviewed the house policies and resident daily rhythm.</li><li>Medication storage, meal preferences, and first-week observation plan were confirmed.</li></ul>",
      questions: [
        ["What daily routines should staff follow from the first day?", "Morning coffee by 8:00 AM, light breakfast, and family call after dinner."],
        ["What items, equipment, or comfort preferences came with the resident?", "Walker, preferred blanket, framed family photo, and medication organizer."],
        ["Who should staff contact for urgent family updates?", "Elaine Johnson, daughter and PoA."],
      ],
    },
    "resident-30-day-probation": {
      startDate: probationTwoDaysAgo,
      endDate: dateInputValue(shiftDate(new Date(), 28)),
      status: "Active observation",
      description:
        "Thirty-day probation observation period for behavior, health, welfare, medication routines, care needs, and discrepancies between state assessment, Meet and Greet, and actual home routines.",
      dailyNotes: [
        {
          noteDate: probationYesterday,
          date: `${probationYesterday}T16:30:00`,
          title: "Evening transition note",
          addedBy: "Daniel Reed",
          updatedBy: "Sarah Mitchell",
          updatedAt: `${probationYesterday}T17:05:00`,
          category: "Behavior and welfare",
          summary: "Resident was calm after dinner, requested family call, and used walker with standby support.",
          notes: "<p>Resident was calm after dinner and requested a family call.</p><ul><li>Used walker with standby support.</li><li>No medication refusal observed.</li></ul>",
          photos: [{ fileName: "walker-position.jpg", description: "Walker positioned near recliner before evening transfer." }],
        },
        {
          noteDate: probationTwoDaysAgo,
          date: `${probationTwoDaysAgo}T09:15:00`,
          title: "Morning care note",
          addedBy: "Sarah Mitchell",
          updatedBy: "Sarah Mitchell",
          updatedAt: `${probationTwoDaysAgo}T09:40:00`,
          category: "Health and medication",
          summary: "Medication routine completed on schedule. Mild fatigue observed during transfer from bed to chair.",
          notes: "<p>Medication routine completed on schedule.</p><p>Mild fatigue observed during transfer from bed to chair.</p>",
          photos: [],
        },
        {
          noteDate: probationOlderDay,
          date: `${probationOlderDay}T12:10:00`,
          title: "Meal preference update",
          addedBy: "Maya Torres",
          updatedBy: "Maya Torres",
          updatedAt: `${probationOlderDay}T12:20:00`,
          category: "Care preference",
          summary: "Resident prefers warm tea before lunch and needs reminders to use non-slip footwear.",
          notes: "<p>Resident prefers warm tea before lunch and responded well to gentle footwear reminders.</p>",
          photos: [],
        },
      ],
      assignedStaff: [
        { name: "Sarah Mitchell", role: "Lead caregiver", from: `${probationYesterday}T07:00:00`, to: `${probationYesterday}T15:00:00` },
        { name: "Daniel Reed", role: "Care aide", from: `${probationYesterday}T15:00:00`, to: `${probationYesterday}T23:00:00` },
        { name: "Maya Torres", role: "Medication aide", from: `${probationTwoDaysAgo}T08:00:00`, to: `${probationTwoDaysAgo}T12:00:00` },
      ],
    },
    "preliminary-plan": {
      status: "Draft",
      templateName: "Tab 15_4 Preliminary Service Plan (1) (2).docx",
      createdAt: `${probationTwoDaysAgo}T13:15:00`,
      createdBy: "Sarah Mitchell",
      latestGeneratedAt: `${probationYesterday}T09:30:00`,
      latestGeneratedBy: "Olreadia Care Assistant",
      lastUpdatedAt: `${probationYesterday}T15:45:00`,
      lastUpdatedBy: "Sarah Mitchell",
      generationLocked: false,
      description:
        "<h3>Preliminary Service Plan</h3><p><strong>Generated from state assessment, Meet and Greet, onboarding inputs, and early observation notes.</strong></p><h4>Resident Profile and Responsible Parties</h4><p>Resident: Margaret Johnson. Responsible party/PoA: Elaine Johnson, daughter. Other involved family/care circle members include Robert Johnson and Linda Green. Confirm age, birth date, move-in date, spouse/family history, siblings, children, grandchildren, close friends, and preferred family contact details during the next family meeting.</p><h4>Personal History, Religion, Interests, and Social Preferences</h4><p>Resident prefers quiet mornings, coffee, music after lunch, and a family call after dinner. Staff should learn more about important celebrations, religious/denomination preferences, church visits, communion needs, pets, hobbies, group preferences, favorite music, and whether resident leaves the home for special events.</p><h4>Food Preferences and Dietary Needs</h4><p>Resident prefers low-salt meals, warm tea before lunch, and light breakfast routines. Confirm breakfast/lunch/dinner preferred times, favorite snacks, dining-room versus bedroom preference, alcohol permission, specialized diet orders, favorite foods, dislikes, beverages, meats, vegetables, fruits, and comfort-food recipes.</p><h4>Communication, Sensory, and Safety</h4><p>Resident communicates verbally and responds best to calm, respectful cues. Confirm speech, hearing, eyesight, telephone use, preferred language, need for speech therapy assessment, bedroom lock preference, privacy curtain preference, allergies, and evacuation level. Resident requires standby assistance and clear walkways for evacuation.</p><h4>Medication, Treatments, and Health Monitoring</h4><p>Medication routine requires prompts and monitoring for refusal. Confirm self-administration status, nurse delegation, injections, oxygen, pain issues, community programs, vital signs, blood sugar checks, weight monitoring, wound care, home health, PT, OT, speech therapy, RN support, and hospice involvement.</p><h4>Mental Capacity, Attitude, and Behavior</h4><p>Resident has been calm and receptive in early notes. Continue documenting mood, orientation, confusion, agitation, forgetfulness, wandering, elopement risk, door alarm need, psycho-pharmacological medication use, location checks, and any behaviors from the state assessment with matching interventions.</p><h4>Mobility, Transfers, Bed Mobility, and Equipment</h4><p>Resident uses walker with standby support and has mild transfer fatigue. Keep walker within reach near recliner and bed. Confirm ambulation level, wheelchair/cane/crutches use, one-person or two-person assist, transfer method, lift needs, paralysis, restorative range of motion, bed repositioning frequency, and equipment such as hospital bed, gait belt, transfer board, bath bench, oxygen, pressure mattress, blood pressure machine, and O2 saturation meter.</p><h4>Continence, Toileting, Bathing, Dressing, Skin, Grooming, Mouth Care, and Eating</h4><p>Continue observing continence, toileting routine, bowel movement frequency, dressing support, bath type and bath days, skin care, contractures or pressure areas, grooming, podiatry, dentures/mouth care, feeding ability, clothing protector needs, special equipment during meals, fluid restriction or encouragement, thickened liquids, and hydration schedule.</p><h4>Goals, Preferences, and Refusal Plan</h4><p>Resident goals and preferences should be confirmed with resident and family. If medication is refused, notify PCP at the time of refusal and document the event. Ask whether other refusal behaviors occur and document how staff should manage them using the least restrictive, resident-centered approach.</p>",
      dataSources: [
        "Initial Assessment",
        "Meet and Greet",
        "Client Onboarding",
        "Resident 30-Day Probation",
        "Daily Notes",
      ],
      attachedFiles: [
        { type: "document", name: "Tab 15_4 Preliminary Service Plan (1) (2).docx", caption: "Preliminary service plan source template" },
        { type: "document", name: "Preliminary care plan draft.pdf", caption: "Generated preliminary plan draft" },
        { type: "photo", name: "walker-position.jpg", caption: "Transfer support photo from probation note" },
        { type: "document", name: "Medication list.pdf", caption: "Medication source file used for plan generation" },
      ],
    },
    "annual-residency-progress": {
      startDate: "2024-05-08",
      endDate: "2025-05-08",
      status: "Active annual progress",
      description:
        "Annual residency progress period for ongoing resident care patterns, status changes, care coordination, daily notes, family updates, and new items that may affect the negotiated care plan.",
      evaluationNotes:
        "<p><strong>Overall evaluation/status notes:</strong> Resident remains stable with continued need for calm routines, medication prompts, transfer support, and family communication after dinner.</p><ul><li>No urgent welfare concerns noted in the current progress window.</li><li>Continue monitoring transfer fatigue and hydration reminders.</li><li>Daily notes should be reviewed before updating the negotiated care plan.</li></ul>",
      dailyNotes: [
        {
          noteDate: probationYesterday,
          date: `${probationYesterday}T18:20:00`,
          title: "Annual progress daily status",
          addedBy: "Sarah Mitchell",
          updatedBy: "Sarah Mitchell",
          updatedAt: `${probationYesterday}T18:45:00`,
          category: "Welfare",
          summary: "Resident remained engaged during evening routine and completed family call without distress.",
          notes: "<p>Resident remained engaged during evening routine and completed family call without distress.</p><ul><li>Hydration reminders were accepted.</li><li>No new medication concerns reported.</li></ul>",
          photos: [],
        },
        {
          noteDate: probationTwoDaysAgo,
          date: `${probationTwoDaysAgo}T10:30:00`,
          title: "Mobility observation",
          addedBy: "Daniel Reed",
          updatedBy: "Maya Torres",
          updatedAt: `${probationTwoDaysAgo}T11:05:00`,
          category: "Mobility",
          summary: "Resident used walker with standby assist and reported mild fatigue after breakfast.",
          notes: "<p>Resident used walker with standby assist and reported mild fatigue after breakfast.</p>",
          photos: [{ fileName: "walker-check.jpg", description: "Walker placed within reach near breakfast table." }],
        },
      ],
      assignedStaff: [
        { name: "Sarah Mitchell", role: "Lead caregiver", from: `${probationYesterday}T07:00:00`, to: `${probationYesterday}T15:00:00` },
        { name: "Daniel Reed", role: "Care aide", from: `${probationYesterday}T15:00:00`, to: `${probationYesterday}T23:00:00` },
      ],
    },
    "negotiated-plan": {
      status: "Draft",
      templateName: "AFH HCS NCP-Template 10.11.23.docx",
      formVersion: "3.12.2024",
      createdAt: `${probationYesterday}T10:15:00`,
      createdBy: "Sarah Mitchell",
      latestGeneratedAt: `${probationYesterday}T11:05:00`,
      latestGeneratedBy: "Olreadia Care Assistant",
      lastUpdatedAt: `${probationYesterday}T12:20:00`,
      lastUpdatedBy: "Sarah Mitchell",
      dataSources: [
        "Initial Assessment",
        "Meet and Greet",
        "Client Onboarding",
        "Preliminary Plan",
        "Resident 30-Day Probation",
        "Annual Residency Progress",
        "Daily Notes",
      ],
      sections: [
        {
          key: "resident-profile",
          title: "Resident and Provider Information",
          helper: "Provider name, NCP dates, moved-in date, resident name, pronouns, DOB/age, primary language, allergies, legal documents, and specialty needs.",
          value:
            "Provider: Olreadia Care. Resident: Margaret Johnson. Primary language: English. Interpreter needed: No. Allergies and legal document status should be verified against the state assessment and admission packet before finalization.",
        },
        {
          key: "evacuation-safety",
          title: "Emergency Evacuation and Safety Instructions",
          helper: "Evacuation assistance required, safety instructions, and caregiver evacuation responsibilities.",
          value:
            "Assistance required. Resident needs standby assistance, walker access, clear walkways, and calm cueing during evacuation. Caregiver will keep evacuation routes clear and guide resident using short, direct instructions.",
        },
        {
          key: "medical-status",
          title: "Mental/Physical Health - Current Medical Status/Diagnosis",
          helper: "Current diagnosis, medical status, allergies, health indicators, pain, and other issues or concerns.",
          value:
            "State assessment and early care notes indicate transfer fatigue, medication prompting needs, and ongoing monitoring for hydration, mobility, welfare, and changes in behavior. No acute welfare concerns noted during the current observation window.",
        },
        {
          key: "responsible-parties",
          title: "Responsible Parties - Contacts",
          helper: "Case manager, DPOA, guardian, medical providers, pharmacy, hospice, and preferred contact methods.",
          value:
            "Amanda Parker, Social Worker. Elaine Johnson, Daughter / PoA, preferred family contact. Robert Johnson, Son. Care team should confirm phone, email, pharmacy, PCP, and other provider contact details before completion.",
        },
        {
          key: "communication",
          title: "Communication, Speech, Hearing, and Vision",
          helper: "Modes of expression, hearing/vision concerns, phone use, preferred language, strengths, preferences, assistance, and caregiver instructions.",
          value:
            "Resident communicates needs verbally and benefits from calm, respectful explanations. Resident prefers family calls after dinner and responds well to gentle reminders. No interpreter need has been identified.",
        },
        {
          key: "medication-management",
          title: "Medication Management",
          helper: "Medication assistance type, MAR reference, pharmacy packing, nurse delegation, refusal plan, and caregiver instructions.",
          value:
            "Resident requires medication prompts and monitoring for refusal or adverse reactions. Caregiver will follow physician orders, MAR documentation, the five rights plus documentation, and report significant concerns immediately.",
        },
        {
          key: "treatments-programs",
          title: "Treatments, Programs, Therapies, and Health Indicators",
          helper: "Pain, weight, vital signs, therapies, home health, hospice, meaningful day, physical enablers, and refusal plan.",
          value:
            "Continue monitoring mobility, transfer fatigue, hydration, and any treatment or therapy instructions received from PCP, PT, OT, or nurse delegation. Meaningful day activities should remain part of the support plan.",
        },
        {
          key: "psych-social-cognitive",
          title: "Psych/Social/Cognitive Status",
          helper: "Sleep, memory, decision making, behaviors, crisis plan, mental health supports, interventions, left-alone status, and narrative day.",
          value:
            "Resident benefits from a predictable routine, quiet breakfast, music after lunch, and family contact. No disruptive or unsafe behavior has been observed in current notes, but staff should document changes promptly.",
        },
        {
          key: "adls",
          title: "Activities of Daily Living",
          helper: "Ambulation, transfers, eating, toileting, dressing, hygiene, bathing, foot care, skin care, strengths, preferences, assistance, and caregiver instructions.",
          value:
            "Resident uses walker with standby support and may experience mild transfer fatigue. Caregiver will maintain walker within reach, cue use of non-slip footwear, support transfers, and monitor for falls, skin concerns, and fatigue.",
        },
        {
          key: "iadls-activities",
          title: "Instrumental Activities, Social, Transportation, and Case Management",
          helper: "Finances, shopping, transportation, activities, smoking, case management, other issues, and resident recommendations.",
          value:
            "Resident enjoys quiet routines, warm tea before lunch, music after lunch, and family contact. Transportation, shopping, finances, and case-management details should be confirmed with PoA and social worker.",
        },
        {
          key: "review-approval",
          title: "Negotiated Care Plan Review and Approval",
          helper: "Original plan date, review/revision dates, people involved, approvals, signatures, verbal agreement, DSHS CM sent date, and resident recommendations.",
          value:
            "Resident and resident representative should participate to the extent possible. Final plan should be reviewed with provider, resident/representative, and DSHS case manager before confirmation and signature collection.",
        },
      ],
      attachedFiles: [
        { type: "document", name: "AFH HCS NCP-Template 10.11.23.docx", caption: "Locked source template" },
      ],
    },
  },
};

export default function AssessmentsPage() {
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [processes, setProcesses] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState(["ACTIVE"]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [startMode, setStartMode] = useState("NEW");
  const [selectedResidentId, setSelectedResidentId] = useState("");
  const [startingAssessment, setStartingAssessment] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    if (!workspaceListReady) return () => {
      isMounted = false;
    };
    if (!selectedWorkspaceId) {
      setProcesses([]);
      setError("Select a workspace to view assessment processes.");
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }
    setLoading(true);
    careService.assessmentProcesses
      .listMini({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId, size: 100 })
      .then((data) => {
        if (!isMounted) return;
        setProcesses(dedupeAssessmentProcessList(data));
        setError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setProcesses([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load assessment processes.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    setResidents([]);
    setSelectedResidentId("");
    setResidentsLoading(false);
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (!startDialogOpen || startMode !== "CURRENT" || residents.length || !selectedWorkspaceId) return undefined;
    let active = true;
    setResidentsLoading(true);
    careService.residents
      .listMini({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId, size: 100 })
      .then((data) => {
        if (!active) return;
        setResidents(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!active) return;
        setResidents([]);
      })
      .finally(() => {
        if (active) setResidentsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [residents.length, selectedWorkspaceId, startDialogOpen, startMode]);

  const residentsById = useMemo(() => {
    const map = new Map();
    residents.forEach((resident) => {
      const id = residentIdFor(resident);
      if (id) map.set(String(id), resident);
    });
    return map;
  }, [residents]);

  const summary = useMemo(() => {
    const active = processes.filter((process) => !isClosedProcessStatus(process)).length;
    const newResidents = processes.filter((process) => assessmentResidentType(process) === "NEW").length;
    const currentResidents = processes.filter((process) => assessmentResidentType(process) === "RESIDENT").length;
    return { active, newResidents, currentResidents };
  }, [processes]);

  const filteredProcesses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filters = selectedFilters.length ? selectedFilters : ["ALL"];
    const showAll = filters.includes("ALL");
    const requireActive = filters.includes("ACTIVE");
    const residentTypeFilters = filters.filter((value) => ["NEW", "RESIDENT"].includes(value));
    return processes.filter((process) => {
      const residentType = assessmentResidentType(process);
      const active = !isClosedProcessStatus(process);
      const filterMatches =
        showAll ||
        ((!requireActive || active) &&
          (!residentTypeFilters.length || residentTypeFilters.includes(residentType)));

      if (!filterMatches) return false;
      if (!query) return true;
      const residentRecord = residentsById.get(String(processResidentId(process))) || null;

      return [
        processResidentName(process, residentRecord),
        assessmentDisplayName(process, residentRecord),
        processSocialWorkerName(process),
        processWorkflowStatus(process),
        residentType,
        ...processRecords(process).map(recordLabel),
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [processes, residentsById, searchQuery, selectedFilters]);

  const residentOptions = useMemo(
    () =>
      residents
        .map((resident) => {
          const id = residentIdFor(resident);
          if (!id) return null;
          return {
            value: id,
            label: residentOptionLabel(resident),
          };
        })
        .filter(Boolean),
    [residents],
  );

  const totalPages = Math.max(1, Math.ceil(filteredProcesses.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedProcesses = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredProcesses.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredProcesses, pageSize]);
  const paginationStart = filteredProcesses.length ? ((currentPage - 1) * pageSize) + 1 : 0;
  const paginationEnd = Math.min(filteredProcesses.length, currentPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [pageSize, searchQuery, selectedFilters]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const closeStartDialog = () => {
    setStartDialogOpen(false);
    setStartMode("NEW");
    setSelectedResidentId("");
  };

  const startAssessment = async () => {
    if (startMode === "NEW") {
      closeStartDialog();
      navigate("/assessments/new");
      return;
    }

    if (!selectedResidentId || startingAssessment) return;
    const selectedResident = residents.find((resident) => String(residentIdFor(resident)) === String(selectedResidentId)) || {};
    setStartingAssessment(true);
    try {
      const payload = {
        assessmentType: "ANNUAL_ASSESSMENT",
        patientType: "RESIDENT",
        residentId: selectedResidentId,
        residentName: residentNameFor(selectedResident),
        startDate: new Date().toISOString().slice(0, 10),
        status: "OPEN",
        stage: "YEARLY_STATE_ASSESSMENT",
      };
      const created = await careService.assessmentProcesses.create(payload);
      const createdId = processId(created) || created?.id || created?.assessmentProcessId;
      if (!createdId) throw new Error("Assessment process was created without an id.");
      setProcesses((current) => dedupeAssessmentProcessList([created || { ...payload, id: createdId }, ...current]));
      closeStartDialog();
      navigate(`/assessments/${createdId}/new`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to start assessment process.");
    } finally {
      setStartingAssessment(false);
    }
  };

  const filterPills = [
    ["ACTIVE", "Active"],
    ["NEW", "New Resident"],
    ["RESIDENT", "Current Resident"],
    ["ALL", "All"],
  ];
  const toggleFilter = (value) => {
    setSelectedFilters((current) => {
      if (value === "ALL") return ["ALL"];
      const withoutAll = current.filter((item) => item !== "ALL");
      const next = withoutAll.includes(value)
        ? withoutAll.filter((item) => item !== value)
        : [...withoutAll, value];
      return next.length ? next : ["ALL"];
    });
  };

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Assessments</h1>
          <p>Track resident assessments from state assessment through ongoing review and final confirmation.</p>
        </div>
        <div className="residents-actions">
          <Guard roles={["care_owner", "care_admin"]}>
            <button className="btn primary" type="button" onClick={() => setStartDialogOpen(true)}>
              <i className="ti ti-player-play" /> Start Assessment
            </button>
          </Guard>
          <button className="mini-btn"><i className="ti ti-filter" /> Filter</button>
        </div>
      </div>

      <div className="resident-summary-grid">
        {[
          ["ti-clipboard-text", "Total assessments", processes.length, "In this workspace"],
          ["ti-player-play", "Active", summary.active, "Need action"],
          ["ti-user-plus", "New residents", summary.newResidents, "Onboarding flows"],
          ["ti-user-check", "Current residents", summary.currentResidents, "Continuing care reviews"],
        ].map(([icon, label, value, note]) => (
          <article className="resident-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      <Panel title="Assessment Processes">
        <div className="resident-list-toolbar">
          <label className="resident-search">
            <i className="ti ti-search" />
            <input
              placeholder="Search by resident, social worker, status, or attached record"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </label>
          <div className="resident-filter-pills">
            {filterPills.map(([value, label]) => (
              <button
                className={selectedFilters.includes(value) ? "is-active" : ""}
                type="button"
                onClick={() => toggleFilter(value)}
                key={value}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {error ? <p className="form-error">{error}</p> : null}

        <div className="assessment-process-table">
          <div className="assessment-process-row assessment-process-head">
            <span>Resident Name</span>
            <span>Resident Type</span>
            <span>Start Date</span>
            <span>Social Worker</span>
            <span>Status</span>
            <span />
          </div>
          {loading ? <p className="assessment-empty-state">Loading assessment processes...</p> : null}
          {!loading && !filteredProcesses.length && !error ? <p className="assessment-empty-state">No assessment processes found.</p> : null}
          {paginatedProcesses.map((process) => {
            const assessmentProcessId = processId(process);
            const status = processWorkflowStatus(process) || "Open";
            const residentRecord = residentsById.get(String(processResidentId(process))) || null;
            const displayResidentName = processResidentName(process, residentRecord);
            return (
            <button className="assessment-process-row" type="button" onClick={() => navigate(`/assessments/${assessmentProcessId}/new`)} key={assessmentProcessId || `${displayResidentName}-${process.assessmentDate || process.startDate || process.processStartDate}`}>
              <span>
                <strong>{displayResidentName}</strong>
                <small>{assessmentDisplayName(process, residentRecord)}</small>
              </span>
              <span><em className="pill blue">{assessmentResidentTypeLabel(process)}</em></span>
              <span><strong>{formatDate(process.processStartDate || process.assessmentDate || process.startDate)}</strong></span>
              <span>{processSocialWorkerName(process)}</span>
              <span><em className={`pill ${processStatusClass(status)}`}>{status}</em></span>
              <span className="resident-row-actions"><i className="ti ti-chevron-right" /></span>
            </button>
            );
          })}
        </div>
        {filteredProcesses.length ? (
          <div className="timesheet-pagination assessment-pagination">
            <span>
              Showing <strong>{paginationStart}-{paginationEnd}</strong> of <strong>{filteredProcesses.length}</strong>
            </span>
            <label>
              Rows
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {ASSESSMENT_PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="timesheet-pagination-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage <= 1}
              >
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={currentPage >= totalPages}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        ) : null}
      </Panel>

      {startDialogOpen ? (
        <div className="assessment-start-dialog" role="dialog" aria-modal="true" aria-label="Start Assessment">
          <button className="assessment-start-dialog__backdrop" type="button" aria-label="Close start assessment" onClick={closeStartDialog} />
          <div className="assessment-start-dialog__panel">
            <div className="assessment-start-dialog__head">
              <div>
                <strong>Start Assessment</strong>
                <small>Select the assessment flow to begin.</small>
              </div>
              <button type="button" className="icon-btn" onClick={closeStartDialog} aria-label="Close">
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="assessment-start-options">
              <button
                type="button"
                className={startMode === "NEW" ? "is-selected" : ""}
                onClick={() => setStartMode("NEW")}
              >
                <i className="ti ti-user-plus" />
                <span>
                  <strong>New Resident</strong>
                  <small>Begin a new resident assessment flow.</small>
                </span>
              </button>
              <button
                type="button"
                className={startMode === "CURRENT" ? "is-selected" : ""}
                onClick={() => setStartMode("CURRENT")}
              >
                <i className="ti ti-user-check" />
                <span>
                  <strong>Current Resident</strong>
                  <small>Select an existing resident for continuing review.</small>
                </span>
              </button>
            </div>

            {startMode === "CURRENT" ? (
              <div className="assessment-current-resident-picker">
                <Select
                  className="assessment-current-resident-select"
                  popupClassName="assessment-current-resident-select-popup"
                  getPopupContainer={(triggerNode) =>
                    triggerNode.closest(".assessment-start-dialog__panel") || document.body
                  }
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={residentsLoading ? "Loading residents..." : "Select resident"}
                  value={selectedResidentId || undefined}
                  onChange={(value) => setSelectedResidentId(value || "")}
                  loading={residentsLoading}
                  disabled={residentsLoading}
                  options={residentOptions}
                />
              </div>
            ) : null}

            <div className="assessment-start-dialog__actions">
              <button className="btn secondary" type="button" onClick={closeStartDialog}>Cancel</button>
              <button className="btn primary" type="button" onClick={startAssessment} disabled={startingAssessment || (startMode === "CURRENT" && !selectedResidentId)}>
                {startingAssessment ? "Starting..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CareShell>
  );
}
